import { sleep } from '~/common/utils/common.utils.js';

/**
 * Composable that provides a `withRetry` utility for retrying async operations.
 *
 * @example
 * const { withRetry } = useRetry();
 * await withRetry(() => $services.features.post({ ... }));
 *
 * // Override defaults:
 * await withRetry(() => someApiCall(), { retries: 5, delay: 1000 });
 */
export function useRetry() {
  /**
   * Retries an async function on failure.
   *
   * @param {() => Promise<any>} fn - The async function to execute.
   * @param {{ retries?: number, delay?: number }} [options]
   * @param {number} [options.retries=3]  - Max number of attempts.
   * @param {number} [options.delay=3000] - Milliseconds to wait between attempts.
   * @returns {Promise<any>}
   */
  async function withRetry(fn, { retries = 3, delay = 3000 } = {}) {
    let lastError;
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        return await fn();
      }
      catch (err) {
        lastError = err;
        if (attempt < retries)
          await sleep(delay);
      }
    }
    throw lastError;
  }

  return { withRetry };
}
